import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '../../../../store';
import { createRoadUpdate } from '../../../../utils/roadHelpers';
import { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import { useRoadMapping } from '@/hooks/useRoadMapping';
import { hasCoordinatesChangedSignificantly } from '@/utils/geolocation/distanceCalculation';
import { WIZARD_CONSTANTS } from '../types/constants/constants';

// Helper: Check if coordinates are valid for GPS detection
const isValidForGPSDetection = (coordinates: Coordinates | null): boolean => {
  if (!coordinates) return false;
  if (
    typeof coordinates.lat !== 'number' ||
    typeof coordinates.lng !== 'number'
  )
    return false;
  return !isNaN(coordinates.lat) && !isNaN(coordinates.lng);
};

// Helper: Check if GPS coordinates changed significantly
const shouldUpdateFromGPS = (
  coordinates: Coordinates,
  lastCoordinates: Coordinates | null
): boolean => {
  return hasCoordinatesChangedSignificantly(
    coordinates,
    lastCoordinates,
    WIZARD_CONSTANTS.GPS_COORDINATE_THRESHOLD_METERS
  );
};
import type { UseTaskWizardFormReturn } from '../utils/mantineFormSchema';
import type { Task, Coordinates } from '../../../../types';
import type { RoadSource } from '../utils/roadSourceUtils';

interface UseRoadDetectionHookProps {
  form: UseTaskWizardFormReturn['form'];
  editingTask?: Task | null;
  gpsLocationSuggestion: ReturnType<typeof useGPSLocationSuggestion>;
  onGPSComplete?: () => void;
}

export const useRoadDetectionHook = ({
  form,
  editingTask,
  gpsLocationSuggestion,
  onGPSComplete,
}: UseRoadDetectionHookProps) => {
  const tempRoad = useGeoMarkupStore((state) => state.map.tempRoad);
  const setTempRoad = useGeoMarkupStore((state) => state.map.setTempRoad);

  // Use anchorPoint from Zustand (single source of truth) for GPS detection
  const anchorPoint = useGeoMarkupStore((state) => state.tasks.formAnchorPoint);

  const [forceGPSDetection, setForceGPSDetection] = useState(false);
  const [roadSource, setRoadSource] = useState<RoadSource>(null);

  // Track last detected anchor for change detection
  const [lastDetectedAnchor, setLastDetectedAnchor] =
    useState<Coordinates | null>(editingTask?.anchorPoint || null);
  const [manuallyCleared, setManuallyCleared] = useState(false);
  const [isDetectionInProgress, setIsDetectionInProgress] = useState(false);

  // Skip initial GPS detection during edit mode to preserve saved values
  const skipInitialDetection = useMemo(() => !!editingTask, [editingTask]);
  const hasRunInitialDetection = useRef(false);

  const { t } = useTranslation();
  const { mapGPSToRoad } = useRoadMapping(t);

  // Synchronize lastDetectedAnchor when anchor point changes
  // This tracks the last GPS-detected position to prevent redundant detections
  useEffect(() => {
    if (editingTask?.anchorPoint) {
      setLastDetectedAnchor(editingTask.anchorPoint);
    } else {
      setLastDetectedAnchor(null);
    }
    // We use primitives (lat, lng) in deps to avoid infinite loops from object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask?.anchorPoint?.lat, editingTask?.anchorPoint?.lng]);

  // Reset state when switching between tasks (create ↔ edit mode)
  // This ensures clean state and prevents stale GPS suggestions
  useEffect(() => {
    if (editingTask) {
      // EDIT MODE: Reset roadSource (road value comes from database, not GPS)
      // This ensures GPS badge is not shown for database values
      setRoadSource(null);
      setManuallyCleared(false);
      gpsLocationSuggestion.clearSuggestion();
    } else {
      // CREATE MODE: Only reset manually cleared flag
      // Keep roadSource to preserve GPS badge after detection
      setManuallyCleared(false);
    }

    // Reset detection flag when switching tasks/modes to ensure skipInitialDetection works correctly
    hasRunInitialDetection.current = false;
    // We use editingTask?.id instead of editingTask to avoid re-running on object reference changes
    // Note: gpsLocationSuggestion intentionally NOT in deps to prevent resetting roadSource after GPS detect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask?.id]);

  // Note: Removed formRef and gpsLocationSuggestionRef workarounds
  // Now using form and gpsLocationSuggestion directly in dependencies
  // This is the correct React pattern - refs were an anti-pattern

  // Handle road detection from map clicks (tempRoad staging area)
  useEffect(() => {
    if (tempRoad) {
      const roadUpdate = createRoadUpdate(tempRoad, true);

      // Map shortName to full formatted name
      const fullName = mapGPSToRoad(roadUpdate.road || '');

      form.setFieldValue('location', fullName);
      form.setFieldValue('locationFromMap', roadUpdate.roadFromMap);

      // Clear tempRoad after using it
      setTempRoad(null);
    }
  }, [tempRoad, form, setTempRoad, mapGPSToRoad]);

  // GPS detection logic - uses anchorPoint for location detection
  const detectRoad = useCallback(async () => {
    // Guard: Anchor point must exist and be valid
    if (!isValidForGPSDetection(anchorPoint) || isDetectionInProgress) {
      return;
    }
    if (!anchorPoint) {
      return;
    }

    // Skip initial auto-detection during edit mode to preserve saved values
    if (
      skipInitialDetection &&
      !hasRunInitialDetection.current &&
      !forceGPSDetection
    ) {
      hasRunInitialDetection.current = true;
      return; // Exit early - don't run GPS detection this first time
    }

    const coordinatesChanged = shouldUpdateFromGPS(
      anchorPoint,
      lastDetectedAnchor
    );

    // Check if GPS detection should be triggered
    const shouldTriggerDetection =
      (coordinatesChanged || forceGPSDetection) &&
      !manuallyCleared &&
      !tempRoad;

    if (shouldTriggerDetection) {
      try {
        setIsDetectionInProgress(true);

        // Clear existing GPS-detected road for fresh detection
        if (roadSource === 'gps' && form.values.location) {
          form.setFieldValue('location', '');
        }

        // Use anchor point for GPS detection
        await gpsLocationSuggestion.detectLocation(anchorPoint);
        setLastDetectedAnchor(anchorPoint);

        if (forceGPSDetection) {
          setForceGPSDetection(false);
        }
      } catch (error) {
        // Log error in development for debugging
        if (import.meta.env.DEV) {
          console.warn('[useRoadDetectionHook] GPS detection failed:', error);
        }
        if (forceGPSDetection) {
          setForceGPSDetection(false);
        }
      } finally {
        setIsDetectionInProgress(false);
      }
    }
    // We use anchorPoint?.lat and anchorPoint?.lng (primitives) in deps instead of anchorPoint (object)
    // to avoid infinite loops from object reference changes. ESLint complains about this pattern,
    // but it's correct for our use case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    anchorPoint?.lat,
    anchorPoint?.lng,
    lastDetectedAnchor,
    manuallyCleared,
    roadSource,
    tempRoad,
    forceGPSDetection,
    isDetectionInProgress,
    skipInitialDetection,
    form,
    gpsLocationSuggestion,
  ]);

  // Trigger GPS detection when dependencies change
  useEffect(() => {
    detectRoad();
  }, [detectRoad]);

  // Apply GPS suggestion when available
  // Note: Stale suggestions are cleared in reset effect, so only fresh suggestions apply
  const applyGPSSuggestion = useCallback(() => {
    if (gpsLocationSuggestion.suggestion && !manuallyCleared && !tempRoad) {
      const suggestion = gpsLocationSuggestion.suggestion;
      const mappedRoad = mapGPSToRoad(suggestion.suggestion);

      if (mappedRoad) {
        form.setFieldValue('location', mappedRoad);
        form.setFieldValue('locationFromMap', false);
        setRoadSource('gps');
      }

      gpsLocationSuggestion.clearSuggestion();
      onGPSComplete?.(); // Signal completion
    }
  }, [
    gpsLocationSuggestion,
    manuallyCleared,
    tempRoad,
    mapGPSToRoad,
    form,
    onGPSComplete,
  ]);

  // Trigger GPS suggestion application when suggestion changes
  useEffect(() => {
    applyGPSSuggestion();
  }, [gpsLocationSuggestion.suggestion, applyGPSSuggestion]);

  // Handler to force GPS detection
  const handleRequestGPSDetection = useCallback(() => {
    setManuallyCleared(false);
    setForceGPSDetection(true);
  }, []);

  // Handler for manual road input (typing or selecting from list)
  const handleManualRoadInput = useCallback(
    (value: string) => {
      if (value === '') {
        // Clear road field completely
        form.setFieldValue('location', '');
        form.setFieldValue('locationFromMap', false);
        setRoadSource(null);
        setManuallyCleared(true);
        gpsLocationSuggestion.clearSuggestion();
        setTempRoad(null);
      } else {
        // Manual input - set road value
        form.setFieldValue('location', value);
        setRoadSource('manual');
      }
    },
    [form, gpsLocationSuggestion, setTempRoad]
  );

  return {
    roadSource,
    handleRequestGPSDetection,
    handleManualRoadInput,
  };
};
